import { useMemo } from 'react'

import { usePaymentSchedule } from '../application/product/paymentScheduleContext'
import {
  AdditionalPriceOption,
  ConditionalCover,
  CoverOption,
  CoverType,
  getAdditionalPriceBreakdown,
  getCovers,
  getCoversByCoverType,
  getPremium,
  getPriceBreakdown,
  getTaxAmount,
  getTotalPremium,
  PriceOption,
  Promotion,
  Quote,
} from '../domain'
import {
  getCoreAssetType,
  PaymentScheduleType,
  ProductConfig,
} from '../state/configuration'

export const useIncludedCovers = (product: ProductConfig): string[] =>
  useMemo(() => getCoreAssetType(product).covers.included, [product])

export const useMainCovers = (quote: Quote | undefined): PriceOption[] => {
  const { paymentScheduleType } = usePaymentSchedule()

  return useMemo(() => {
    if (!quote) return []

    const priceBreakdown = getPriceBreakdown(quote, paymentScheduleType)
    if (!priceBreakdown) return []

    return getCoversByCoverType(priceBreakdown, CoverType.MAIN)
  }, [quote, paymentScheduleType])
}

export const useConditionalCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
  paymentType?: PaymentScheduleType,
): ConditionalCover[] =>
  useMemo(() => {
    if (!quote) return []

    const { covers } = getCoreAssetType(product)

    const priceBreakdown = getPriceBreakdown(quote, paymentType)
    if (!priceBreakdown) return []

    const activeCovers: Set<string> = new Set(
      getCoversByCoverType(priceBreakdown, CoverType.MAIN)
        .filter((it) => it.active)
        .flatMap((it) => it.covers ?? []),
    )

    return (
      covers.conditional?.map((coverCode) => ({
        coverCode,
        active: activeCovers.has(coverCode),
      })) ?? []
    )
  }, [quote, product, paymentType])

export const useOptionalCovers = (
  quote: Quote | undefined,
  product: ProductConfig,
): CoverOption[] => {
  const { paymentScheduleType } = usePaymentSchedule()

  return useMemo(() => {
    if (!quote) return []

    const { covers } = getCoreAssetType(product)
    const priceBreakdown = getPriceBreakdown(quote, paymentScheduleType)

    return (covers.optional ?? []).map((cover) => {
      const priceOptions = getCovers(priceBreakdown ?? [], cover.code)
      const priceOption = priceOptions.at(0)

      return {
        coverGroup: cover.code,
        priceOptions,
        available: Boolean(priceOption),
        active: priceOption ? priceOptions.every((it) => it.active) : undefined,
        insurer: priceOption?.insurer,
        premium: getPremium(priceOptions),
        totalPremium: getTotalPremium(priceOptions),
        taxAmount: getTaxAmount(priceOptions),
      } satisfies CoverOption
    })
  }, [product, quote, paymentScheduleType])
}

export const usePromotion = (quote: Quote | undefined): Promotion | undefined => {
  const { paymentScheduleType } = usePaymentSchedule()

  return useMemo(() => {
    const coverLevelOption = quote?.availableCoverLevelOptions.find(
      (it) => it.paymentScheduleType === paymentScheduleType && it.active,
    )

    return coverLevelOption?.promotion
  }, [quote, paymentScheduleType])
}

export const useAdditionalPremiumBreakdown = (
  quote: Quote | undefined,
): AdditionalPriceOption[] => {
  const { paymentScheduleType } = usePaymentSchedule()

  return useMemo(() => {
    if (!quote) return []
    return getAdditionalPriceBreakdown(quote, paymentScheduleType) ?? []
  }, [quote, paymentScheduleType])
}
